/**
 * Panel import / export / status & bulk-delete handlers.
 * Ported from the monolith's addJuror / exportPanelJSON / import* / setJurorStatus /
 * deleteAllJurors* functions. Import/dedup/id-assignment is delegated to
 * loadJurorArray in @/features/panel.
 */

import JSZip from 'jszip';
import type { Juror } from '@/types';
import { store } from '@/app/store';
import { byId, inputValue, setInputValue } from '@/ui/dom';
import { showToast, confirmApp } from '@/ui/overlays';
import { showPage, currentPageId } from '@/ui/router';
import { renderPanel, updatePanelBadge, loadJurorArray, openJurorDetail } from '@/features/panel';

/** m/d/yyyy — matches the affidavit date field's expected format. */
function todayStr(): string {
  const d = new Date();
  return d.getMonth() + 1 + '/' + d.getDate() + '/' + d.getFullYear();
}

/** Clear the "Add Juror" form back to its default state. */
function clearAddForm(): void {
  setInputValue('add-juror-num', '');
  setInputValue('add-occ', '');
  setInputValue('add-notes', '');
  [
    'add-seat', 'add-gender', 'add-age', 'add-edu', 'add-emp-status', 'add-biz-type',
    'add-hh-size', 'add-parent', 'add-jury', 'add-le', 'add-law-emp', 'add-victim',
    'add-accused', 'add-fair', 'add-case-know', 'add-opinion'
  ].forEach((id) => {
    const el = byId<HTMLSelectElement>(id);
    if (el) el.selectedIndex = 0;
  });
}

/** Add a single juror from the "Add Juror" form to the panel. */
export function addJuror(): void {
  const state = store.getState();
  const occ = inputValue('add-occ');
  const seat = inputValue('add-seat');
  const notes = inputValue('add-notes');
  if (!occ && !notes && !inputValue('add-biz-type')) {
    showToast('Please fill in at least occupation or business type');
    return;
  }
  const panelId = inputValue('add-juror-num') || 'J-' + String(state.jurors.length + 1).padStart(2, '0');
  const j: Juror = {
    id: state.nextId++,
    panelId,
    seat: parseInt(seat, 10) || state.jurors.length + 1,
    gender: inputValue('add-gender'),
    age: inputValue('add-age'),
    edu: inputValue('add-edu'),
    empStatus: inputValue('add-emp-status'),
    bizType: inputValue('add-biz-type'),
    occ,
    hhSize: inputValue('add-hh-size'),
    parent: inputValue('add-parent'),
    jury: inputValue('add-jury'),
    le: inputValue('add-le'),
    lawEmp: inputValue('add-law-emp'),
    victim: inputValue('add-victim'),
    accused: inputValue('add-accused'),
    fair: inputValue('add-fair'),
    caseKnow: inputValue('add-case-know'),
    opinion: inputValue('add-opinion'),
    notes,
    status: 'none',
    tags: []
  };
  state.jurors.push(j);
  store.save();
  updatePanelBadge();
  showToast(panelId + ' added to panel');
  clearAddForm();
}

/**
 * Load imported jurors via the shared panel loader (dedups + assigns ids),
 * then refresh the panel and navigate to it. Mirrors the tail of the
 * monolith's loadJurorArray (badge + "N loaded" toast + showPage).
 */
function importJurors(arr: Juror[]): void {
  const before = store.getState().jurors.length;
  loadJurorArray(arr);
  const loaded = store.getState().jurors.length - before;
  updatePanelBadge();
  renderPanel();
  showToast(loaded + ' juror' + (loaded === 1 ? '' : 's') + ' loaded');
  showPage('panel');
}

/** Open the file picker for panel import. */
export function importFromFile(): void {
  const input = byId<HTMLInputElement>('import-file-input');
  if (!input) return;
  input.value = '';
  input.click();
}

/** Export the current panel to a downloadable JSON file (permitted fields only). */
export function exportPanelJSON(): void {
  const state = store.getState();
  if (state.jurors.length === 0) {
    showToast('No jurors to export');
    return;
  }
  const exportData = state.jurors.map((j) => ({
    panelId: j.panelId, seat: j.seat, gender: j.gender, age: j.age, edu: j.edu,
    empStatus: j.empStatus, bizType: j.bizType, occ: j.occ, hhSize: j.hhSize,
    parent: j.parent, jury: j.jury, le: j.le, lawEmp: j.lawEmp,
    victim: j.victim, accused: j.accused, fair: j.fair,
    caseKnow: j.caseKnow, opinion: j.opinion, notes: j.notes,
    status: j.status, tags: j.tags
  }));
  const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'spatchai-panel-export.json';
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
  showToast(exportData.length + ' jurors exported');
}

/** Route a picked file to the JSON or ZIP importer by extension. */
export function handleImportFile(input: HTMLInputElement): void {
  const file = input.files?.[0];
  if (!file) return;
  const name = file.name.toLowerCase();
  if (name.endsWith('.zip')) {
    handleZipImport(file);
  } else if (name.endsWith('.json')) {
    handleJSONImport(file);
  } else {
    showToast('Unsupported file type — use .json or .zip');
  }
  // Reset so same file can be re-selected
  input.value = '';
}

/** Parse a .json file (SpatchAI export or array of juror objects). */
export function handleJSONImport(file: File): void {
  const reader = new FileReader();
  reader.onload = (e) => {
    try {
      const data = JSON.parse(String(e.target?.result));
      const arr = Array.isArray(data)
        ? data
        : (data && data.jurors && Array.isArray(data.jurors) ? data.jurors : null);
      if (!arr || arr.length === 0) {
        showToast('No juror data found in file');
        return;
      }
      confirmApp(
        'Import ' + arr.length + ' Juror' + (arr.length === 1 ? '' : 's'),
        'Import ' + arr.length + ' juror profile' + (arr.length === 1 ? '' : 's') + ' from ' + file.name + '? Duplicates (matching Panel ID) are skipped.',
        'Import', 'btn-primary'
      ).then((ok) => { if (ok) importJurors(arr); });
    } catch (err) {
      showToast('JSON parse error: ' + (err as Error).message);
    }
  };
  reader.readAsText(file);
}

/** Parse a .zip file containing individual juror JSON files or a combined JSON. */
export function handleZipImport(file: File): void {
  const reader = new FileReader();
  reader.onload = (e) => {
    JSZip.loadAsync(e.target?.result as ArrayBuffer).then((zip) => {
      const jsonFiles: JSZip.JSZipObject[] = [];
      zip.forEach((_relPath, zipEntry) => {
        if (zipEntry.name.toLowerCase().endsWith('.json') && !zipEntry.dir) {
          jsonFiles.push(zipEntry);
        }
      });
      if (jsonFiles.length === 0) {
        showToast('No JSON files found in zip');
        return;
      }
      const promises = jsonFiles.map((entry) => entry.async('string'));
      Promise.all(promises).then((texts) => {
        let allJurors: Juror[] = [];
        texts.forEach((text, idx) => {
          try {
            const data = JSON.parse(text);
            if (Array.isArray(data)) { allJurors = allJurors.concat(data); }
            else if (data && typeof data === 'object' && data.panelId) { allJurors.push(data); }
            else if (data && data.jurors && Array.isArray(data.jurors)) { allJurors = allJurors.concat(data.jurors); }
          } catch (err) {
            console.warn('Could not parse ' + jsonFiles[idx].name + ': ' + (err as Error).message);
          }
        });
        if (allJurors.length === 0) {
          showToast('No valid juror records found in zip JSON files');
          return;
        }
        confirmApp(
          'Import ' + allJurors.length + ' Juror' + (allJurors.length === 1 ? '' : 's'),
          'Import ' + allJurors.length + ' juror profile' + (allJurors.length === 1 ? '' : 's') + ' from ' + file.name + ' (' + jsonFiles.length + ' file' + (jsonFiles.length === 1 ? '' : 's') + ')? Duplicates are skipped.',
          'Import', 'btn-primary'
        ).then((ok) => { if (ok) importJurors(allJurors); });
      });
    }).catch((err) => { showToast('Zip error: ' + (err as Error).message); });
  };
  reader.readAsArrayBuffer(file);
}

/** Set a juror's strike/keep status, persist, and refresh the open detail + panel. */
export function setJurorStatus(id: number, status: string): void {
  const j = store.getState().jurors.find((x) => x.id === id);
  if (!j) return;
  j.status = status;
  store.save();
  openJurorDetail(id);
  if (currentPageId() === 'panel') renderPanel();
}

/** Permanently delete every juror while preserving case data (Data-tab entry point). */
export async function deleteAllJurors(): Promise<void> {
  const state = store.getState();
  const jurorCount = state.jurors ? state.jurors.length : 0;
  if (jurorCount === 0) {
    showToast('No jurors on file — nothing to delete');
    return;
  }
  const caseName = state.caseName || '(unnamed case)';
  const ok = await confirmApp(
    'Destroy All Juror Data',
    'Permanently delete all ' + jurorCount + ' juror record(s)? Case information for "' + caseName + '" will be preserved. This cannot be undone.',
    'Delete All', 'btn-danger'
  );
  if (!ok) {
    showToast('Deletion canceled');
    return;
  }
  state.jurors = [];
  state.nextId = 100;
  state.lastJurorDeletion = { count: jurorCount, timestamp: todayStr() };
  store.save();
  updatePanelBadge();
  renderPanel();
  showToast('All ' + jurorCount + ' juror record(s) deleted — case data preserved');
}

/** Permanently delete every juror while preserving case data (Panel-tab entry point). */
export async function deleteAllJurorsFromPanel(): Promise<void> {
  const state = store.getState();
  const jurorCount = state.jurors ? state.jurors.length : 0;
  if (jurorCount === 0) {
    showToast('No jurors on file — nothing to delete');
    return;
  }
  const caseName = state.caseName || '(unnamed case)';
  const ok = await confirmApp(
    'Delete All Juror Data',
    'Permanently delete all ' + jurorCount + ' juror record(s)? Case information for "' + caseName + '" will be preserved. This cannot be undone.',
    'Delete All', 'btn-danger'
  );
  if (!ok) {
    showToast('Deletion canceled');
    return;
  }
  state.jurors = [];
  state.nextId = 100;
  state.lastJurorDeletion = { count: jurorCount, timestamp: todayStr() };
  store.save();
  updatePanelBadge();
  renderPanel();
  showToast('All ' + jurorCount + ' juror record(s) deleted — case data preserved');
}
